import { map, reduce, flatten, groupBy, orderBy } from 'lodash';
import parseNumber from '../utils/parseNumber';

export const sumValues = (data, fields, initialValue) => {
  const initial = { ...initialValue };
  fields.forEach(field => initial[field] = 0);

  return reduce(
    data,
    (output, current) => {
      for (const field of fields) {
        output[field] += parseNumber(current[field]);
      }
      return output;
    },
    initial
  );
};

export const sumActiveUsersByApplianceByTenanthMonth = (records, fields) => {
  const appliances = groupBy(records, 'root_domain_auth');

  return orderBy(
    flatten(
      map(appliances, (appliance, applianceName) => {
        const tenants = groupBy(appliance, 'tenant');

        return flatten(
          map(tenants, (tenant, tenantName) => {
            const months = groupBy(tenant, 'month');

            return map(months, (month, monthName) => {
              return sumValues(
                month,
                fields, {
                  month: monthName,
                  tenant: tenantName,
                  root_domain_auth: applianceName,
                  environment: appliance[0].environment,
                }
              );
            });
          })
        );
      })
    ),
    ['month'],
    ['desc']
  );
};

export const activeUserByAppplianceByTenantMonth = records => {
  return sumActiveUsersByApplianceByTenanthMonth(
    records, ['users_regular', 'users_enterprise']);
};

export const externalActiveUsersByAppplianceByTenantMonth = records => {
  return sumActiveUsersByApplianceByTenanthMonth(
    records, ['users_external']);
};

export const sumActiveUsersByApplianceMonth = (records, fields) => {
  const appliances = groupBy(records, 'root_domain_auth');

  return orderBy(
    flatten(
      map(appliances, (appliance, applianceName) => {
        const months = groupBy(appliance, 'month');

        return map(months, (month, monthName) => {
          return sumValues(
            month,
            fields, {
              month: monthName,
              root_domain_auth: applianceName,
              environment: appliance[0].environment,
            }
          );
        });
      })
    ),
    ['month'],
    ['desc']
  );
};

export const activeUserByApplianceMonth = records => {
  return sumActiveUsersByApplianceMonth(records, ['users_regular', 'users_enterprise']);
};

export const externalActiveUsersByApplianceMonth = records => {
  return sumActiveUsersByApplianceMonth(records, ['users_external']);
};

export const sumActiveUsersByTenantMonth = (records, fields) => {
  const tenants = groupBy(records, 'tenant');

  return orderBy(
    flatten(
      map(tenants, (tenant, tenantName) => {
        const months = groupBy(tenant, 'month');

        return map(months, (month, monthName) => {
          return sumValues(
            month,
            fields, {
              month: monthName,
              tenant: tenantName
            }
          );
        });
      })
    ),
    ['month'],
    ['desc']
  );
};

export const activeUserByTenantMonth = records => {
  return sumActiveUsersByTenantMonth(records, ['users_regular', 'users_enterprise']);
};

export const externalActiveUsersByTenantMonth = (records) => {
  return sumActiveUsersByTenantMonth(records, ['users_external']);
};

export const sumActiveUsersByMonth = (records, fields) => {
  const months = groupBy(records, 'month');

  return map(months, (month, monthName) => {
    return sumValues(
      month,
      fields, {
        month: monthName,
      });
  });
};

export const activeUserByMonth = records => {
  return sumActiveUsersByMonth(records, ['users_regular', 'users_enterprise']);
};

export const externalActiveUsersByMonth = (records) => {
  return sumActiveUsersByMonth(records, ['users_external']);
};

export const employeeUserByAppplianceByTenantMonth = records => {
  const appliances = groupBy(records, 'root_domain_auth');

  return orderBy(
    flatten(
      map(appliances, (appliance, applianceName) => {
        const tenants = groupBy(appliance, 'tenant');

        return flatten(
          map(tenants, (tenant, tenantName) => {
            const months = groupBy(tenant, 'month');

            return map(months, (month, monthName) => {
              return sumValues(
                month,
                ['all_internal_users'], {
                  month: monthName,
                  tenant: tenantName,
                  root_domain_auth: applianceName,
                  environment: appliance[0].environment
                }
              );
            });
          })
        );
      })
    ),
    ['month'],
    ['desc']
  );
};

export const employeeUserByApplianceMonth = records => {
  const appliances = groupBy(records, 'root_domain_auth');

  return orderBy(
    flatten(
      map(appliances, (appliance, applianceName) => {
        const months = groupBy(appliance, 'month');

        return map(months, (month, monthName) => {
          return sumValues(
            month,
            ['all_internal_users'], {
              month: monthName,
              root_domain_auth: applianceName,
              environment: appliance[0].environment
            }
          );
        });
      })
    ),
    ['month'],
    ['desc']
  );
};

export const employeeUserByTenantMonth = records => {
  const tenants = groupBy(records, 'tenant');

  return orderBy(
    flatten(
      map(tenants, (tenant, tenantName) => {
        const months = groupBy(tenant, 'month');

        return map(months, (month, monthName) => {
          return sumValues(
            month,
            ['all_internal_users'], {
              month: monthName,
              tenant: tenantName
            }
          );
        });
      })
    ),
    ['month'],
    ['desc']
  );
};

export const employeeUserByMonth = records => {
  const months = groupBy(records, 'month');

  return map(months, (month, monthName) => {
    return sumValues(
      month,
      ['all_internal_users'], {
        month: monthName
      }
    );
  });
};

export const mfaUserByTenantMonth = records => {
  const tenants = groupBy(records, 'tenant');

  return orderBy(
    flatten(
      map(tenants, (tenant, tenantName) => {
        const months = groupBy(tenant, 'month');

        return map(months, (month, monthName) => {
          return sumValues(
            month,
            ['mfa_consumer', 'mfa_enterprise', 'mfa_guardian_addon'], {
              month: monthName,
              tenant: tenantName
            }
          );
        });
      })
    ),
    ['month'],
    ['desc']
  );
};

export const mfaUserByMonth = records => {
  const months = groupBy(records, 'month');

  return map(months, (month, monthName) => {
    return sumValues(
      month,
      ['mfa_consumer', 'mfa_enterprise', 'mfa_guardian_addon'], {
        month: monthName
      }
    );
  });
};

export const mfaUserByApplianceMonth = records => {
  const appliances = groupBy(records, 'root_domain_auth');

  return orderBy(
    flatten(
      map(appliances, (appliance, applianceName) => {
        const months = groupBy(appliance, 'month');

        return map(months, (month, monthName) => {
          return sumValues(
            month,
            ['mfa_consumer', 'mfa_enterprise', 'mfa_guardian_addon'], {
              month: monthName,
              root_domain_auth: applianceName,
              environment: appliance[0].environment,
            }
          );
        });
      })
    ),
    ['month'],
    ['desc']
  );
};

export const mfaUsersByAppplianceByTenantMonth = records => {
  const appliances = groupBy(records, 'root_domain_auth');

  return orderBy(
    flatten(
      map(appliances, (appliance, applianceName) => {
        const tenants = groupBy(appliance, 'tenant');

        return flatten(
          map(tenants, (tenant, tenantName) => {
            const months = groupBy(tenant, 'month');

            return map(months, (month, monthName) => {
              return sumValues(
                month,
                ['mfa_consumer', 'mfa_enterprise', 'mfa_guardian_addon'], {
                  month: monthName,
                  tenant: tenantName,
                  root_domain_auth: applianceName,
                  environment: appliance[0].environment
                }
              );
            });
          })
        );
      })
    ),
    ['month'],
    ['desc']
  );
};

export const apiCallsByTenantMonth = records => {
  const tenants = groupBy(records, 'tenant');

  return orderBy(
    flatten(
      map(tenants, (tenant, tenantName) => {
        const months = groupBy(tenant, 'month');

        return map(months, (month, monthName) => {
          return sumValues(
            month,
            ['api_calls'], {
              month: monthName,
              tenant: tenantName
            }
          );
        });
      })
    ),
    ['month'],
    ['desc']
  );
};

export const apiCallsByMonth = records => {
  const months = groupBy(records, 'month');

  return map(months, (month, monthName) => {
    return sumValues(
      month,
      ['api_calls'], {
        month: monthName
      }
    );
  });
};

export const apiCallsByApplianceMonth = records => {
  const appliances = groupBy(records, 'root_domain_auth');

  return orderBy(
    flatten(
      map(appliances, (appliance, applianceName) => {
        const months = groupBy(appliance, 'month');

        return map(months, (month, monthName) => {
          return sumValues(
            month,
            ['api_calls'], {
              month: monthName,
              environment: appliance[0].environment,
              root_domain_auth: applianceName,
            }
          );
        });
      })
    ),
    ['month'],
    ['desc']
  );
};

export const apiCallsByAppplianceByTenantMonth = records => {
  const appliances = groupBy(records, 'root_domain_auth');

  return orderBy(
    flatten(
      map(appliances, (appliance, applianceName) => {
        const tenants = groupBy(appliance, 'tenant');

        return flatten(
          map(tenants, (tenant, tenantName) => {
            const months = groupBy(tenant, 'month');

            return map(months, (month, monthName) => {
              return sumValues(
                month,
                ['api_calls'], {
                  month: monthName,
                  tenant: tenantName,
                  root_domain_auth: applianceName,
                  environment: appliance[0].environment
                }
              );
            });
          })
        );
      })
    ),
    ['month'],
    ['desc']
  );
};

export const activeConnectionsByTenantMonth = records => {
  const tenants = groupBy(records, 'tenant');

  return orderBy(flatten(
    map(tenants, (tenant, tenantName) => {
      const connections = groupBy(tenant, 'connection_id');

      return flatten(
        map(connections, (connection, connectionId) => {
          const months = groupBy(connection, 'month');

          return map(months, (month, monthName) => {
            return sumValues(
              month,
              ['active_users'], {
                month: monthName,
                tenant: tenantName,
                connection_id: connectionId,
                connection_name: connection[0].connection_name
              }
            );
          });
        })
      );
    })
  ), ['month'], ['desc']);
};

export const perConnectionsByMonth = records => {
  const months = groupBy(records, 'month');

  return map(months, (month, monthName) => {
    return sumValues(
      month,
      ['active_connections'], {
        month: monthName
      }
    );
  });
};

export const perConnectionsByAppplianceByConnectionMonth = records => {
  const appliances = groupBy(records, 'root_domain_auth');

  return orderBy(
    flatten(
      map(appliances, (appliance, applianceName) => {
        const connections = groupBy(records, 'connection_name');

        return flatten(
          map(connections, (connection, connectionName) => {
            const tenants = groupBy(connection, 'tenant');

            return flatten(
              map(tenants, (tenant, tenantName) => {
                const months = groupBy(tenant, 'month');

                return map(months, (month, monthName) => {
                  return sumValues(
                    month,
                    ['active_users'], {
                      month: monthName,
                      tenant: tenantName,
                      connection_name: connectionName,
                      root_domain_auth: applianceName,
                      environment: appliance[0].environment
                    }
                  );
                });
              })
            );
          })
        );
      })
    ),
    ['month'],
    ['desc']
  );
};

export const activeConnectionsByMonth = (records) => {
    if(!records) {
        return;
    } 
    let tempRecords = orderBy(records, ['month'], ['desc']);
    let updatedRecords = {};
    for (let i = 0; i < tempRecords.length; i++) {
        if (
            updatedRecords[records[i].month] &&
            updatedRecords[records[i].month].size
        ) {
            updatedRecords[records[i].month].add(records[i].connection_id);
        } else {
            updatedRecords[records[i].month] = new Set();
            updatedRecords[records[i].month].add(records[i].connection_id);
        }
    }

    let normalizedRecords = [];
    for(const key in updatedRecords) {
        normalizedRecords.push({
            month: key,
            active_connections: updatedRecords[key].size,
        });
    }
    return normalizedRecords;
};

export const activeConnectionsByPrivateInstance = (records) => {
    const appliances = groupBy(records, 'root_domain_auth');
    return orderBy(
        flatten(
            map(appliances, (appliance, applianceName) => {
                const months = groupBy(appliance, 'month');
                return flatten(map(months, (month, monthName) => {
                    
                    const connectionsSet = new Set();
                    month.forEach(m => {
                        connectionsSet.add(m.connection_id); 
                    });
                    
                    return sumValues(
                    month,
                    [],{
                        month: monthName,
                        root_domain_auth: applianceName,
                        active_connections: connectionsSet.size,
                        environment: appliance[0].environment,
                    }
                    );
                }));
            })
        ), 
        ['month'],
        ['desc']
    );
};

export const activeConnectionsByApplianceByTenantMonth = (records) => {
    const appliances = groupBy(records, 'root_domain_auth');
    return orderBy(
        flatten(
            map(appliances, (appliance, applianceName) => {
                const tenants = groupBy(appliance, 'tenant');
                return flatten(
                    map(tenants, (tenant, tenantName) => {
                        const connections = groupBy(tenant, 'connection_id');
                        return flatten(
                            map(connections, (connection, connectionId) => {
                                const months = groupBy(connection, 'month');
                                return map(months, (month, monthName) => {
                                    return sumValues(
                                    month,
                                    ['active_users'],{
                                        month: monthName,
                                        root_domain_auth: applianceName,
                                        tenant: tenantName,
                                        connection_id: connectionId,
                                        connection_name: connection[0].connection_name,
                                        environment: appliance[0].environment,
                                    });
                                });
                            })
                        );
                    })
                );
            })
        ), 
        ['month'],
        ['desc']
    );
};
